'use client';

import { Body, Label } from '../typography';

export interface InfiniteScrollFooterProps {
  totalCount?: number;
  totalCountLabel?: string;
  isLoading?: boolean;
}

export function InfiniteScrollFooter({ totalCount, totalCountLabel, isLoading }: InfiniteScrollFooterProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-2 w-full">
        <Body className="text-content-secondary" size="sm">
          Loading more...
        </Body>
      </div>
    );
  }

  if (!totalCount) {
    return null;
  }

  return (
    <div className="border-t pt-4">
      <div className="flex items-center justify-center">
        <Label className="text-content-secondary">{`${totalCount} ${totalCountLabel || 'items'}`}</Label>
      </div>
    </div>
  );
}

export interface InfiniteScrollSentinelProps {
  /** Ref returned by the infinite scroll hook; observed to trigger the next page load */
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  /** Whether more pages can be loaded. Renders nothing when false */
  hasNextPage: boolean;
  /** Whether the next page is currently being fetched */
  isFetchingNextPage: boolean;
}

/**
 * Sentinel element observed by infinite scroll hooks, wrapping the loading footer.
 * Place it after the list or table body so the next page loads as it scrolls into view.
 */
export function InfiniteScrollSentinel({ sentinelRef, hasNextPage, isFetchingNextPage }: InfiniteScrollSentinelProps) {
  if (!hasNextPage) {
    return null;
  }

  return (
    <div className="w-full" ref={sentinelRef}>
      <InfiniteScrollFooter isLoading={isFetchingNextPage} />
    </div>
  );
}
